import React, { useRef } from 'react';
import PDFThumbnail from './PDFThumbnail';
import { truncateText } from '../utils/textUtils';
import { IoClose, IoAddCircle, IoLanguage } from "react-icons/io5";
import { useTranslation } from 'react-i18next';
import HorizontalShelf from './HorizontalShelf';

const RecentPDFs = ({
  sortedPdfs,
  onOpenPdf,
  onRemoveFromRecent,
  onAddPdf,
  isDarkMode,
}) => {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      onAddPdf(file);
      event.target.value = '';
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const formatCasBlock = (block) => {
    if (!block) return null;

    const isEnglish = i18n.language === 'en';
    const cleanBlock = block.replace('B', '');

    return isEnglish ? `CAS: B${cleanBlock}` : `中科院: ${cleanBlock}区`;
  };

  const buildMetricPills = (pdf) => {
    const pills = [];

    if (pdf.journalMetrics?.if) {
      pills.push({
        key: 'if',
        label: `IF: ${pdf.journalMetrics.if}`,
        className: isDarkMode ? 'bg-blue-500/15 text-blue-100' : 'bg-blue-100 text-blue-800',
      });
    }

    if (pdf.journalMetrics?.q) {
      pills.push({
        key: 'q',
        label: `Q: ${pdf.journalMetrics.q}`,
        className: isDarkMode ? 'bg-emerald-500/15 text-emerald-100' : 'bg-emerald-100 text-emerald-800',
      });
    }

    if (pdf.journalMetrics?.b) {
      pills.push({
        key: 'b',
        label: formatCasBlock(pdf.journalMetrics.b),
        className: isDarkMode ? 'bg-amber-500/15 text-amber-100' : 'bg-amber-100 text-amber-800',
      });
    }

    return pills;
  };

  return (
    <section className={`relative shrink-0 overflow-hidden rounded-[32px] border p-4 md:p-5 ${
      isDarkMode
        ? 'border-white/10 bg-[#081120]/85 shadow-[0_30px_90px_rgba(2,6,23,0.55)]'
        : 'border-slate-200/90 bg-white/90 shadow-[0_28px_90px_rgba(148,163,184,0.22)]'
    }`}>
      <div className={`absolute inset-0 ${
        isDarkMode
          ? 'bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.20),transparent_30%),radial-gradient(circle_at_80%_20%,rgba(236,72,153,0.14),transparent_24%),linear-gradient(180deg,rgba(15,23,42,0.45),rgba(8,17,32,0.95))]'
          : 'bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.12),transparent_30%),radial-gradient(circle_at_80%_20%,rgba(236,72,153,0.10),transparent_24%),linear-gradient(180deg,rgba(248,250,252,0.78),rgba(255,255,255,0.92))]'
      }`} />

      <div className="relative">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className={`text-2xl font-semibold tracking-tight ${
              isDarkMode ? 'text-white' : 'text-slate-900'
            }`}>
              {t('pdf.recentPDFs')}
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openFilePicker}
              className={`inline-flex items-center gap-2 self-start rounded-full px-3 py-2 text-sm font-semibold ${
                isDarkMode ? 'bg-cyan-400/10 text-cyan-200 hover:bg-cyan-400/20' : 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100'
              }`}
            >
              <IoAddCircle size={16} />
              {t('homeTab.addNewPaper', 'Add a new paper')}
            </button>
            <button
              onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'zh' : 'en')}
              className={`inline-flex items-center gap-2 self-start rounded-full px-3 py-2 text-sm font-medium ${
                isDarkMode ? 'bg-white/6 text-slate-200 hover:bg-white/10' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              title={t('common.changeLanguage')}
            >
              <IoLanguage size={16} />
              {i18n.language === 'en' ? '中文' : 'English'}
            </button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
          className="hidden"
        />

        <HorizontalShelf className="mt-4" isDarkMode={isDarkMode} step={560}>
          {sortedPdfs.length === 0 ? (
            <div className={`flex min-h-[220px] w-full items-center justify-center rounded-[24px] border border-dashed p-6 text-center ${
              isDarkMode ? 'border-white/10 text-slate-400' : 'border-slate-200 text-slate-500'
            }`}>
              <div>
                <p>{t('pdf.noPDFsFound')}</p>
                <button
                  type="button"
                  onClick={openFilePicker}
                  className={`mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${
                    isDarkMode ? 'bg-cyan-400/10 text-cyan-200 hover:bg-cyan-400/20' : 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100'
                  }`}
                >
                  <IoAddCircle size={16} />
                  {t('pdf.clickToUpload')}
                </button>
              </div>
            </div>
          ) : (
            sortedPdfs.map((pdf) => {
              const isDemoPdf = pdf.id === 'sample123';
              const metricPills = buildMetricPills(pdf);

              return (
                <article
                  key={pdf.id}
                  className={`group relative isolate flex h-[344px] w-[272px] shrink-0 snap-start flex-col overflow-hidden rounded-[24px] border p-4 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                    isDarkMode
                      ? 'border-white/10 bg-[#0f1729]/80 shadow-[0_22px_70px_rgba(3,7,18,0.45)]'
                      : 'border-slate-200/80 bg-white/85 shadow-[0_22px_70px_rgba(148,163,184,0.22)]'
                  }`}
                >
                  <button
                    className="absolute right-4 top-4 z-10 rounded-full bg-black/50 p-1 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();

                      if (pdf.id === 'sample123') {
                        localStorage.setItem('sample_pdf_closed', 'true');
                      }

                      onRemoveFromRecent(pdf.id);
                    }}
                  >
                    <IoClose size={14} />
                  </button>

                  <button
                    className="flex h-full flex-col text-left"
                    onClick={() => onOpenPdf(pdf.id)}
                  >
                    <div className="relative h-[202px] overflow-hidden rounded-[18px]">
                      <PDFThumbnail
                        pdfId={pdf.id}
                        metadata={pdf}
                        className="h-full w-full"
                        imageClassName="transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/45 to-transparent" />
                      {isDemoPdf && (
                        <span className="absolute left-3 top-3 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
                          {t('homeTab.demoPaper', 'Demo')}
                        </span>
                      )}
                    </div>

                    <div className="mt-4 flex h-full flex-col">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className={`line-clamp-2 text-base font-semibold ${
                          isDarkMode ? 'text-white' : 'text-slate-900'
                        }`}>
                          {pdf.name}
                        </h3>
                      </div>

                      <p className={`mt-2 text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>
                        <span className={`mr-2 inline-block text-[11px] font-semibold uppercase tracking-[0.2em] ${
                          isDarkMode ? 'text-slate-500' : 'text-slate-400'
                        }`}>
                          {t('recommendedPDF.authors', 'Authors')}:
                        </span>
                        {truncateText(pdf.author || 'Unknown', 36)}
                      </p>

                      <p className={`mt-2 min-h-[20px] line-clamp-1 text-sm font-medium ${
                        isDarkMode ? 'text-slate-200' : 'text-slate-700'
                      } ${pdf.journalName ? '' : 'opacity-0'}`}>
                        {pdf.journalName ? truncateText(pdf.journalName, 44) : 'placeholder'}
                      </p>

                      <div className="mt-4 min-h-[32px] flex flex-wrap gap-2">
                        {metricPills.length > 0 ? (
                          metricPills.map((pill) => (
                            <span key={`${pdf.id}-${pill.key}`} className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs leading-none ${pill.className}`}>
                              {pill.label}
                            </span>
                          ))
                        ) : (
                          <div className="h-[28px]" />
                        )}
                      </div>

                      <div className={`mt-auto pt-4 text-xs ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}>
                        {pdf.lastOpened
                          ? `${t('homeTab.lastOpened', 'Last opened')} ${new Date(pdf.lastOpened).toLocaleDateString()}`
                          : isDemoPdf
                          ? t('homeTab.demoPaperHint', 'Use this demo or upload your own paper to personalize the feed.')
                          : t('homeTab.readyToOpen', 'Ready to reopen')}
                      </div>
                    </div>
                  </button>
                </article>
              );
            })
          )}
        </HorizontalShelf>
      </div>
    </section>
  );
};

export default RecentPDFs;
